// Change #100528 - choose which staff receive a subscriber-based alert.
//
// Staff alerts such as FOOD_RESERVATION_REQUEST_STAFF are sent through
// CommunicationLibrary::triggerForSubscribers(), which mails every active
// CommunicationSubscriber row for that communication. Until now those rows
// could only be changed through the API or the database.
//
// Leavers are hidden from the options but an existing leaver subscription
// still shows (and can be removed), per the active-staff picker rule.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { Label } from "reactstrap";

import { getStaffList } from "../../../../store/staff/action";
import {
  filterActiveStaff,
  staffDisplayName,
} from "../../../../Components/Common/staffActive";
import {
  listCommunicationSubscriberApi,
  createCommunicationSubscriberApi,
  updateCommunicationSubscriberApi,
  deleteCommunicationSubscriberApi,
} from "../../../../helpers/fakebackend_helper";

const flagOn = (v) => v === "1" || v === 1 || v === true;

// Customer-facing communications go to the record's contact, never to
// subscribers, so the picker would do nothing there.
export const usesSubscribers = (communication) =>
  !String(communication?.Code || "").endsWith("_CUSTOMER");

export function useCommunicationSubscribers(communicationId, enabled = true) {
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!communicationId || !enabled) return;
    setLoading(true);
    const res = await listCommunicationSubscriberApi(
      `?Communication_Id=${communicationId}`
    );
    const list = res?.communicationsubscriber_list || [];
    setRows(list);
    setSelectedIds([
      ...new Set(
        list
          .filter((r) => flagOn(r.FlagActive) && r.Staff_Id)
          .map((r) => String(r.Staff_Id))
      ),
    ]);
    setLoading(false);
  }, [communicationId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const activeIds = useMemo(
    () =>
      new Set(
        rows
          .filter((r) => flagOn(r.FlagActive) && r.Staff_Id)
          .map((r) => String(r.Staff_Id))
      ),
    [rows]
  );

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const dirty =
    selected.size !== activeIds.size ||
    [...selected].some((id) => !activeIds.has(id));

  // Applies the difference between the saved rows and the picker.
  // Returns true when every call succeeded.
  const save = async () => {
    if (!dirty) return true;

    const toDelete = rows
      .filter((r) => r.Staff_Id && !selected.has(String(r.Staff_Id)))
      .map((r) => r.Id);

    const toReactivate = [];
    const toCreate = [];
    selected.forEach((staffId) => {
      if (activeIds.has(staffId)) return;
      const inactive = rows.find(
        (r) => String(r.Staff_Id) === staffId && !flagOn(r.FlagActive)
      );
      if (inactive) {
        toReactivate.push({ Id: inactive.Id, FlagActive: "1" });
      } else {
        toCreate.push({
          Communication_Id: communicationId,
          Staff_Id: staffId,
          FlagActive: "1",
        });
      }
    });

    const results = [];
    if (toDelete.length)
      results.push(await deleteCommunicationSubscriberApi(toDelete));
    if (toReactivate.length)
      results.push(await updateCommunicationSubscriberApi(toReactivate));
    if (toCreate.length)
      results.push(await createCommunicationSubscriberApi(toCreate));

    await load();
    return results.every((r) => r?.status === 200);
  };

  return { loading, selectedIds, setSelectedIds, dirty, save };
}

export default function CommunicationSubscribersField({
  loading,
  selectedIds,
  setSelectedIds,
}) {
  const dispatch = useDispatch();
  const staffData = useSelector((state) => state.Staff.staffList?.staff_list);

  useEffect(() => {
    if (!staffData) dispatch(getStaffList());
  }, [dispatch, staffData]);

  // Change #100540 - show the address the alert is mailed to
  // (Staff.ContactEmailAddress, see CommunicationLibrary) so shared or
  // location logins such as "Beach" can be told apart.
  const options = useMemo(
    () =>
      filterActiveStaff(staffData ?? [], selectedIds).map((s) => {
        const name = staffDisplayName(s) || `Staff #${s.Id}`;
        const email = (s.ContactEmailAddress || "").trim();
        return {
          value: String(s.Id),
          label: email ? `${name} (${email})` : `${name} (no email address)`,
        };
      }),
    [staffData, selectedIds]
  );

  // A subscriber whose Staff row is gone still needs a chip so it can be removed.
  const value = selectedIds.map(
    (id) =>
      options.find((o) => o.value === id) || { value: id, label: `Staff #${id}` }
  );

  return (
    <div className="border rounded p-3 mb-3">
      <h6 className="mb-1">
        <i className="ri-team-line me-2"></i>
        Staff subscribers
      </h6>
      <p className="text-muted mb-2">
        <small>
          These staff receive this alert when it fires, on whichever channels
          are enabled below.
        </small>
      </p>
      <Label className="form-label visually-hidden" for="comm-subscribers">
        Staff subscribers
      </Label>
      <Select
        inputId="comm-subscribers"
        isMulti
        isLoading={loading || !staffData}
        options={options}
        value={value}
        onChange={(picked) =>
          setSelectedIds((picked || []).map((p) => p.value))
        }
        placeholder="No one receives this alert - pick staff..."
        classNamePrefix="select2-selection"
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
      />
    </div>
  );
}
